/**
 * SB6-ANA-LIST-02 — per-user density preference for the defects table.
 *
 * Three densities map to row heights:
 *   - "compact"  → 32 px
 *   - "normal"   → 48 px (default)
 *   - "large"    → 64 px
 *
 * Persisted in `localStorage` under `aero.defects.density.${userId}` so a
 * user's preference survives reload and is scoped per logged-in identity
 * (matches the workspace-layout key shape — see `AnalysisWorkspace.tsx`).
 *
 * Locked spec: `.vibe/12_STORY_BOOK_6_FIXES.md` §C1.Q4 — three explicit
 * sizes plus a toggle icon, mirroring the Turbines-page DensityToggle but
 * with three states instead of two.
 */
import { useCallback, useEffect, useState } from "react";

export type DefectDensity = "compact" | "normal" | "large";

export const DEFECT_DENSITY_VALUES: readonly DefectDensity[] = [
  "compact",
  "normal",
  "large",
];

export const DEFECT_DENSITY_ROW_HEIGHT: Record<DefectDensity, number> = {
  compact: 32,
  normal: 48,
  large: 64,
};

const DEFAULT_DENSITY: DefectDensity = "normal";

function storageKey(userId: string): string {
  return `aero.defects.density.${userId}`;
}

function isDensity(v: unknown): v is DefectDensity {
  return v === "compact" || v === "normal" || v === "large";
}

function loadDensity(userId: string): DefectDensity {
  if (!userId) return DEFAULT_DENSITY;
  try {
    const raw = window.localStorage.getItem(storageKey(userId));
    if (raw && isDensity(raw)) return raw;
    return DEFAULT_DENSITY;
  } catch {
    return DEFAULT_DENSITY;
  }
}

function saveDensity(userId: string, next: DefectDensity): void {
  if (!userId) return;
  try {
    window.localStorage.setItem(storageKey(userId), next);
  } catch {
    // private mode / quota — fail silently; the in-memory state still
    // reflects the choice until reload.
  }
}

export interface UseDefectDensityResult {
  density: DefectDensity;
  setDensity: (next: DefectDensity) => void;
  rowHeight: number;
}

export function useDefectDensity(userId: string): UseDefectDensityResult {
  const [density, setDensityState] = useState<DefectDensity>(() =>
    loadDensity(userId),
  );

  // Re-load when the user identity flips (e.g. impersonation chain or
  // logout/login in the same session). The userId is the load key, so a
  // change is the trigger.
  useEffect(() => {
    setDensityState(loadDensity(userId));
  }, [userId]);

  const setDensity = useCallback(
    (next: DefectDensity) => {
      setDensityState(next);
      saveDensity(userId, next);
    },
    [userId],
  );

  return {
    density,
    setDensity,
    rowHeight: DEFECT_DENSITY_ROW_HEIGHT[density],
  };
}
